// ABOUTME: Hook for uploading photo, video, audio and document attachments to the chat.
// Manages file validation, preview generation, upload progress,
// and cleanup of object URLs.

import { useState, useCallback, useEffect, useRef } from 'react'
import { fetchWithAuthRetry, getBaseUrl } from '@/api/client'
import { compressImage } from '@/lib/imageCompression'
import { useAuthStore } from '@/stores/useAuthStore'
import { useMediaUploadStore } from '@/stores/useMediaUploadStore'
import { useUiStore } from '@/stores/useUiStore'
import { startUpload } from '@/platform/upload'
import type { AttachmentRef } from '@/types'
import type { MediaAsset } from '@/types/media'

// Mirrors POST /agent/attachments (app/api/v1/agent/attachments.py): the same
// photo, video and audio mime sets, the same document types, and the same per-kind caps, so the
// client refuses exactly what the server would 413/422 on. A video or recording over its inline
// cap goes up through the resumable uploader instead (see `routeMedia`), which takes up to
// MAX_RESUMABLE_BYTES (MAX_TOTAL_SIZE in app/schemas/media_asset.py), and is attached to the
// message by its asset id all the same. ChatInput derives its `accept` attribute from
// ATTACHMENT_ACCEPT rather than repeating the list — a picker that offers a file
// this hook then rejects is the bug #2034 shipped with.
const ACCEPTED_IMAGE_MIMES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
]
const ACCEPTED_VIDEO_MIMES = ['video/mp4', 'video/quicktime', 'video/webm']
const ACCEPTED_AUDIO_MIMES = [
  'audio/mp4',
  'audio/x-m4a',
  'audio/m4a',
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/ogg',
  'audio/opus',
  'audio/flac',
  'audio/x-flac',
  'audio/webm',
]
// A browser with no codec for a format (HEIC on most desktops, some recordings) gives its file
// no type: its name says what it is, and the upload carries that type.
const MEDIA_EXTENSION_TYPES: Record<string, string> = {
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.m4a': 'audio/mp4',
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.ogg': 'audio/ogg',
  '.oga': 'audio/ogg',
  '.opus': 'audio/ogg',
  '.flac': 'audio/flac',
  '.mov': 'video/quicktime',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
}
// Any other file is sent as it is: the server keeps it in Files (reading its text when it is a
// document) and the agent opens it, so the picker offers every file.
export const ATTACHMENT_ACCEPT = undefined

const MAX_IMAGE_BYTES = 10 * 1024 * 1024 // 10 MB
const MAX_VIDEO_BYTES = 100 * 1024 * 1024 // 100 MB
const MAX_AUDIO_BYTES = 100 * 1024 * 1024 // 100 MB
const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024 // 25 MB
const MAX_RESUMABLE_BYTES = 500 * 1024 * 1024 // 500 MB

export type MediaRoute = 'inline' | 'resumable' | 'too_large'

/** Where a video or recording of this size is uploaded: the chat attachment endpoint, the
 * resumable uploader (still attached to the message), or nowhere. */
export function routeMedia(kind: 'video' | 'audio', sizeBytes: number): MediaRoute {
  if (sizeBytes <= (kind === 'video' ? MAX_VIDEO_BYTES : MAX_AUDIO_BYTES)) return 'inline'
  return sizeBytes <= MAX_RESUMABLE_BYTES ? 'resumable' : 'too_large'
}

export type AttachmentKind = 'image' | 'video' | 'audio' | 'document'

/** The file's media type: its own, or the one its name gives when the browser gave none. */
export function mediaType(file: { type: string; name: string }): string {
  if (file.type && file.type !== 'application/octet-stream') return file.type
  const name = file.name.toLowerCase()
  const ext = Object.keys(MEDIA_EXTENSION_TYPES).find((e) => name.endsWith(e))
  return ext ? MEDIA_EXTENSION_TYPES[ext] : file.type
}

/** The kind this file will be uploaded as: a photo, video or recording, else a file. */
export function attachmentKind(file: { type: string; name: string }): AttachmentKind {
  const type = mediaType(file)
  if (ACCEPTED_IMAGE_MIMES.includes(type)) return 'image'
  if (ACCEPTED_VIDEO_MIMES.includes(type)) return 'video'
  if (ACCEPTED_AUDIO_MIMES.includes(type)) return 'audio'
  return 'document'
}

/** The file as it is uploaded: carrying its media type when the browser gave it none. */
function typed(file: File): File {
  const type = mediaType(file)
  return type === file.type ? file : new File([file], file.name, { type })
}

export interface PendingFile {
  id: string
  file: File
  kind: AttachmentKind
  /** Object URL for a photo the browser can show; empty otherwise — we never decode the file. */
  preview: string
  uploading: boolean
  progress: number
  error: string | null
  uploadId: string | null
  url: string | null
  /** A document's Files id, sent with the message so the agent reads it from Files. */
  fileId: string | null
  /** The resumable uploader's id for a file over its inline cap, uploading since it was picked. */
  resumableId: string | null
  mime_type: string
  filename: string
  size_bytes: number | null
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function toRef(f: PendingFile): AttachmentRef {
  return {
    upload_id: f.uploadId!,
    url: f.url!,
    mime_type: f.mime_type,
    filename: f.filename,
    size_bytes: f.size_bytes!,
    ...(f.fileId ? { file_id: f.fileId } : {}),
  }
}

/** The asset a resumable upload became, once its bytes are all stored (it may still be
 * processing: the message carries its id either way). */
function uploadedAsset(resumableId: string): Promise<MediaAsset> {
  return new Promise((resolve, reject) => {
    const settle = (state: ReturnType<typeof useMediaUploadStore.getState>): boolean => {
      const item = state.uploads[resumableId]
      if (!item) {
        reject(new Error('The upload was cancelled'))
        return true
      }
      if (item.asset && (item.status === 'done' || item.status === 'processing')) {
        resolve(item.asset)
        return true
      }
      if (item.status === 'error' || item.status === 'file_missing') {
        reject(new Error(item.error ?? 'Upload failed'))
        return true
      }
      return false
    }
    if (settle(useMediaUploadStore.getState())) return
    const unsubscribe = useMediaUploadStore.subscribe((state) => {
      if (settle(state)) unsubscribe()
    })
  })
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function useImageUpload() {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  // How many files are mid-compression. Drives the placeholder tiles in the
  // attachment strip and gates send, so a photo can't be dropped by a submit
  // that fires while it's still encoding.
  const [compressingCount, setCompressingCount] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // A message left unsent (the composer goes away) hands its large files back to the upload
  // tray, where they finish into the Library.
  const pendingRef = useRef(pendingFiles)
  useEffect(() => {
    pendingRef.current = pendingFiles
  }, [pendingFiles])
  useEffect(
    () => () => {
      const { holdForMessage } = useMediaUploadStore.getState()
      for (const f of pendingRef.current) {
        if (f.resumableId && !f.uploadId) holdForMessage(f.resumableId, false)
      }
    },
    []
  )

  // Images: pick -> compress -> size check -> POST. Compression runs BEFORE the
  // size check, otherwise an 8 MB phone photo is rejected here and never
  // reaches it. Video and audio take neither step — compressImage is a canvas
  // re-encode and would corrupt or throw on them — so they are routed by size:
  // queued as-is under the 100 MB chat cap, else uploaded through the resumable
  // uploader from the moment they are picked and attached once stored.
  const addFiles = useCallback(async (fileList: FileList | File[]) => {
    const showToast = useUiStore.getState().showToast
    const accepted: { file: File; kind: AttachmentKind }[] = []

    for (const file of Array.from(fileList)) {
      const kind = attachmentKind(file)
      accepted.push({ file: kind === 'document' ? file : typed(file), kind })
    }

    if (accepted.length === 0) return

    const queue = (file: File, kind: AttachmentKind, resumableId: string | null = null) => {
      // HEIC and HEIF are photos most browsers cannot draw: a named tile, like a video's.
      const showable = kind === 'image' && !file.type.startsWith('image/hei')
      setPendingFiles((prev) => [
        ...prev,
        {
          id: generateId(),
          file,
          kind,
          preview: showable ? URL.createObjectURL(file) : '',
          uploading: resumableId !== null,
          progress: 0,
          error: null,
          uploadId: null,
          url: null,
          fileId: null,
          resumableId,
          mime_type: file.type,
          filename: file.name,
          size_bytes: null,
        },
      ])
    }

    const tooLarge = (file: File, max: number, label: string): boolean => {
      if (file.size <= max) return false
      showToast({
        type: 'error',
        title: 'File too large',
        message: `"${file.name}" is ${formatFileSize(file.size)}. Maximum is ${label}.`,
      })
      return true
    }

    // Only photos hold up send — nothing is encoding for a video or a recording.
    const imageCount = accepted.filter((a) => a.kind === 'image').length
    if (imageCount > 0) setCompressingCount((count) => count + imageCount)

    for (const { file: original, kind } of accepted) {
      if (kind === 'document') {
        if (!tooLarge(original, MAX_DOCUMENT_BYTES, '25 MB')) queue(original, 'document')
        continue
      }
      if (kind === 'video' || kind === 'audio') {
        const route = routeMedia(kind, original.size)
        if (route === 'inline') {
          queue(original, kind)
        } else if (route === 'resumable') {
          // Too big for the chat route: it starts up through the resumable uploader now, and
          // the message carries it once it is stored.
          const resumableId = await startUpload(original)
          if (resumableId) {
            queue(original, kind, resumableId)
          } else {
            showToast({
              type: 'error',
              title: 'Upload failed',
              message: `"${original.name}" could not start uploading. Try attaching it again.`,
            })
          }
        } else {
          tooLarge(original, MAX_RESUMABLE_BYTES, '500 MB')
        }
        continue
      }

      try {
        // Returns the original untouched if it's already small, undecodable
        // (HEIC), or the re-encode came out no smaller.
        const file = await compressImage(original)
        if (!tooLarge(file, MAX_IMAGE_BYTES, '10 MB')) queue(file, 'image')
      } finally {
        setCompressingCount((count) => count - 1)
      }
    }
  }, [])

  const removeFile = useCallback((id: string) => {
    setPendingFiles((prev) => {
      const file = prev.find((f) => f.id === id)
      // Video and recording tiles carry no object URL — nothing to revoke.
      if (file?.preview) {
        URL.revokeObjectURL(file.preview)
      }
      // A file taken off the message stops uploading.
      if (file?.resumableId && !file.uploadId) {
        void useMediaUploadStore.getState().removeUpload(file.resumableId)
      }
      return prev.filter((f) => f.id !== id)
    })
  }, [])

  /** Upload every staged file; null when any one could not be uploaded (its tile shows why),
   * so the caller sends nothing rather than a message without the file. */
  const uploadAll = useCallback(async (): Promise<AttachmentRef[] | null> => {
    if (pendingFiles.some((f) => f.error)) return null
    const toUpload = pendingFiles.filter((f) => !f.uploadId)
    if (toUpload.length === 0) {
      // Return already-uploaded files
      return pendingFiles.filter((f) => f.uploadId).map(toRef)
    }

    const baseUrl = getBaseUrl()
    const user = useAuthStore.getState().user
    const businessId = (user as Record<string, unknown> | null)?.default_business_id as
      | string
      | undefined

    const results: AttachmentRef[] = []
    let failed = false

    // Mark all as uploading
    setPendingFiles((prev) =>
      prev.map((f) =>
        toUpload.some((u) => u.id === f.id) ? { ...f, uploading: true, progress: 0 } : f
      )
    )

    // A file over its inline cap is attached by the asset its resumable upload became.
    const attachUploaded = async (pf: PendingFile): Promise<AttachmentRef> => {
      const asset = await uploadedAsset(pf.resumableId!)
      // Sent: the tray shows it while it is still being processed.
      useMediaUploadStore.getState().holdForMessage(pf.resumableId!, false)
      return {
        upload_id: asset.id,
        url: asset.public_url ?? '',
        mime_type: asset.mime_type,
        filename: asset.original_filename ?? pf.filename,
        size_bytes: asset.file_size,
      }
    }

    const postAttachment = async (pf: PendingFile): Promise<AttachmentRef> => {
      const formData = new FormData()
      formData.append('file', pf.file)

      // The token is read per attempt (not hoisted above this loop) so a
      // refresh triggered by an earlier file is picked up by the next one.
      const response = await fetchWithAuthRetry((token) => {
        const headers: Record<string, string> = {}
        if (token) headers['Authorization'] = `Bearer ${token}`
        if (businessId) headers['X-Business-ID'] = businessId
        // No Content-Type — the browser must set the multipart boundary.
        return fetch(`${baseUrl}/agent/attachments`, {
          method: 'POST',
          headers,
          body: formData,
        })
      })

      if (!response.ok) {
        let detail = response.statusText
        try {
          const errBody = await response.json()
          // The API's standard error envelope is {error: {code, message}};
          // reading only `detail` left every rejection showing the bare
          // status text ("Unsupported Media Type") instead of the reason.
          detail = errBody.detail || errBody.message || errBody.error?.message || detail
        } catch {
          // ignore
        }
        throw new Error(detail)
      }
      return (await response.json()) as AttachmentRef
    }

    for (const pf of toUpload) {
      try {
        const data = pf.resumableId ? await attachUploaded(pf) : await postAttachment(pf)

        setPendingFiles((prev) =>
          prev.map((f) =>
            f.id === pf.id
              ? {
                  ...f,
                  uploading: false,
                  progress: 100,
                  uploadId: data.upload_id,
                  url: data.url,
                  fileId: data.file_id ?? null,
                  mime_type: data.mime_type,
                  size_bytes: data.size_bytes,
                }
              : f
          )
        )

        results.push({
          upload_id: data.upload_id,
          url: data.url,
          mime_type: data.mime_type,
          filename: data.filename,
          size_bytes: data.size_bytes,
          ...(data.file_id ? { file_id: data.file_id } : {}),
        })
      } catch (err) {
        failed = true
        const msg = err instanceof Error ? err.message : 'Upload failed'
        setPendingFiles((prev) =>
          prev.map((f) => (f.id === pf.id ? { ...f, uploading: false, error: msg } : f))
        )
        // `pf.error` is only read as a styling flag (ChatInput outlines the tile
        // in red), so the reason has to be toasted or it reaches nobody — the
        // same route the client-side rejections in `addFiles` already take.
        useUiStore.getState().showToast({
          type: 'error',
          title: 'Upload failed',
          message: `"${pf.filename}" — ${msg}`,
        })
      }
    }

    // Also include previously uploaded files
    for (const pf of pendingFiles) {
      if (pf.uploadId && !toUpload.some((u) => u.id === pf.id)) {
        results.push(toRef(pf))
      }
    }

    return failed ? null : results
  }, [pendingFiles])

  const clear = useCallback(() => {
    setPendingFiles((prev) => {
      for (const f of prev) {
        if (f.preview) URL.revokeObjectURL(f.preview)
      }
      return []
    })
  }, [])

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  return {
    pendingFiles,
    compressingCount,
    fileInputRef,
    addFiles,
    removeFile,
    uploadAll,
    clear,
    openFilePicker,
    hasPendingFiles: pendingFiles.length > 0,
  }
}
